/**
 * Panneau « Bénéficiaires commissions » — miroir direct du panneau
 * « Bénéficiaires Royalties » (`components/royalties/RoyaltiesSection.jsx`
 * → BeneficiariesPanel + BeneficiaryDialog).
 *
 * Portée volontairement identique :
 *   - CRUD sur `/api/commission-beneficiaries`
 *   - Choix du contact via la liste des contacts « Contacts & bénéficiaires »
 *   - ID Partenaire externe (majuscules, 3–40 caractères, unique)
 *   - Rattachement à des départements (limités à ceux du contact)
 *   - Sélection restreinte aux contacts avec badge « Partenaire »
 *   - Période contractuelle (start/end) — plus de taux manuel :
 *     les commissions sont automatiquement lues depuis les codes
 *     d'affiliation (page Catégories → Code affiliation) et les sites
 *     (page Sites) rattachés au contact partenaire.
 *
 * Les fonctionnalités spécifiques Royalties (portail partenaire, relevés
 * mensuels, PDF) ne sont volontairement pas répliquées ici car elles n'ont
 * pas de correspondance côté commissions.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Users, Percent, CalendarDays,
  Save, X, Search, AlertCircle, Ticket, MapPin,
  Vault, KeyRound, Copy, Power, UserCog, UserX, RotateCcw, MoreVertical,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import useBeneficiaryCategoryMap from "../../hooks/useBeneficiaryCategoryMap";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import SearchableSelect from "../SearchableSelect";
import HorizontalScrollContainer from "../HorizontalScrollContainer";

const todayIso = () => new Date().toISOString().slice(0, 10);
const dateFR = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");

// Catégories de partenariat (miroir backend PARTNERSHIP_CATEGORIES)
const PARTNERSHIP_CATEGORIES = [
  { value: "agence", label: "Agence affiliée" },
  { value: "pdv", label: "Point de vente affilié" },
  { value: "apporteur", label: "Apporteur affilié" },
  { value: "autre", label: "Autre partenaire" },
];
const partnershipLabel = (v) =>
  PARTNERSHIP_CATEGORIES.find((c) => c.value === v)?.label || "—";

/* ─────────────────────────────────────────────────────────────────────
 * Panel : liste + dialog CRUD
 * ────────────────────────────────────────────────────────────────── */
export default function CommissionBeneficiariesPanel({ isAdmin = true }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState("");
  const [activatingId, setActivatingId] = useState(null);
  const [emailPromptTarget, setEmailPromptTarget] = useState(null);
  const [portalCredentials, setPortalCredentials] = useState(null);
  const [portalDeleteTarget, setPortalDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/commission-beneficiaries");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.contact_name || "").toLowerCase().includes(q) ||
      (it.partner_id || "").toLowerCase().includes(q) ||
      (it.contact_email || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (it) => { setEditing(it); setDialogOpen(true); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/commission-beneficiaries/${deleteTarget.id}`);
      toast.success("Bénéficiaire supprimé");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // ─── Actions Portail Affilié ─────────────────────────────────────
  const doActivatePortal = async (row, emailOverride) => {
    setActivatingId(row.id);
    try {
      const { data } = await api.post(
        `/commission-beneficiaries/${row.id}/activate-portal`,
        { email: emailOverride || null },
      );
      setPortalCredentials({
        title: "Espace affilié activé",
        description: "Transmettez ces identifiants au partenaire. Le mot de passe temporaire ne sera plus visible ensuite.",
        email: data.email,
        temporary_password: data.temporary_password,
        name: data.name,
      });
      setEmailPromptTarget(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur";
      if (typeof detail === "string" && detail.toLowerCase().includes("aucun email")) {
        setEmailPromptTarget({ row, email: "" });
      } else {
        toast.error(detail);
      }
    } finally {
      setActivatingId(null);
    }
  };

  const doResetPassword = async (row) => {
    try {
      const { data } = await api.post(`/commission-beneficiaries/${row.id}/reset-portal-password`);
      setPortalCredentials({
        title: "Mot de passe régénéré",
        description: "Le partenaire devra le changer à sa prochaine connexion.",
        email: data.email,
        temporary_password: data.temporary_password,
        name: data.name,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const doTogglePortal = async (row, is_active) => {
    try {
      await api.patch(`/commission-beneficiaries/${row.id}/portal`, { is_active });
      toast.success(is_active ? "Espace réactivé" : "Espace suspendu");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const doDeletePortal = async () => {
    if (!portalDeleteTarget) return;
    try {
      await api.delete(`/commission-beneficiaries/${portalDeleteTarget.id}/portal`);
      toast.success("Espace affilié supprimé");
      setPortalDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copié");
    } catch { toast.error("Erreur de copie"); }
  };

  return (
    <div className="space-y-4" data-testid="commission-beneficiaries-panel">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-md">
          <div className="relative flex-1">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher (nom, ID partenaire, email)…"
              className="pl-8 h-9"
              data-testid="commission-beneficiaries-search"
            />
          </div>
        </div>
        {isAdmin && (
          <Button
            onClick={openCreate}
            className="bg-[#0055FF] hover:bg-[#0044CC]"
            data-testid="new-commission-beneficiary-btn"
          >
            <Plus size={13} className="mr-1.5" />
            Nouveau bénéficiaire
          </Button>
        )}
      </div>

      {/* Table desktop */}
      <div className="hidden md:block">
        <HorizontalScrollContainer
          testId="commission-beneficiaries-scroll"
          className="bg-white border border-zinc-200 rounded-sm"
        >
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
              <tr>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">ID Partenaire</th>
                <th className="text-left px-4 py-3">Départements</th>
                <th className="text-left px-4 py-3">Commissions code affilié</th>
                <th className="text-left px-4 py-3">Commissions site affilié</th>
                <th className="text-left px-4 py-3 whitespace-nowrap">Période</th>
                <th className="text-left px-4 py-3">Espace</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-zinc-500 font-mono">Chargement…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-zinc-500 font-mono">
                    {search ? "Aucun résultat" : "Aucun bénéficiaire commissions"}
                  </td>
                </tr>
              )}
              {filtered.map((it) => {
                const portal = it.portal || {};
                return (
                <tr key={it.id} className="hover:bg-zinc-50/50">
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-zinc-900" data-testid={`commission-beneficiary-name-${it.id}`}>
                      {it.contact_name}
                    </div>
                    {it.contact_email && (
                      <div className="text-[10px] font-mono text-zinc-500">{it.contact_email}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className="inline-block font-mono text-[11px] px-2 py-0.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-800 uppercase tracking-wider"
                      data-testid={`commission-beneficiary-partner-id-${it.id}`}
                    >
                      {it.partner_id}
                    </span>
                    {it.partnership_category && (
                      <div
                        className="mt-1 text-[10px] text-zinc-500"
                        data-testid={`commission-beneficiary-category-${it.id}`}
                      >
                        {partnershipLabel(it.partnership_category)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(it.brands || []).map((b) => (
                        <span
                          key={b.id}
                          className="text-[10px] px-1.5 py-0.5 rounded-sm font-mono uppercase tracking-wider text-white"
                          style={{ backgroundColor: b.color || "#0055FF" }}
                        >
                          {b.name}
                        </span>
                      ))}
                      {(it.brands || []).length === 0 && (
                        <span className="text-xs text-zinc-400">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5" data-testid={`commission-beneficiary-affiliate-${it.id}`}>
                    <AffiliateSummary codes={it.affiliate_codes} />
                  </td>
                  <td className="px-4 py-2.5" data-testid={`commission-beneficiary-sites-${it.id}`}>
                    <SitesSummary sites={it.partner_sites} />
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-600 whitespace-nowrap">
                    {dateFR(it.start_date)} → {it.end_date ? dateFR(it.end_date) : "∞"}
                  </td>
                  <td className="px-4 py-2.5">
                    {!portal.enabled ? (
                      <Badge variant="outline" className="text-[10px] text-zinc-500" data-testid={`portal-status-${it.id}`}>
                        Non activé
                      </Badge>
                    ) : portal.is_active ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border text-[10px]" data-testid={`portal-status-${it.id}`}>
                        Actif
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-[10px]" data-testid={`portal-status-${it.id}`}>
                        Suspendu
                      </Badge>
                    )}
                    {portal.email && (
                      <div className="text-[10px] text-zinc-500 font-mono mt-0.5 truncate max-w-[180px]" title={portal.email}>
                        {portal.email}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5">
                      {/* Coffre — accès direct au dashboard partenaire affilié (admin/manager) */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/consulter-affiliation/${it.id}`)}
                        data-testid={`open-affiliate-space-${it.id}`}
                        title="Ouvrir l'espace personnel de ce partenaire affilié (mode consultation)"
                        className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-300"
                      >
                        <Vault size={13} />
                      </Button>
                      {isAdmin && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(it)}
                            data-testid={`edit-commission-beneficiary-${it.id}`}
                          >
                            <Pencil size={11} className="mr-1" /> Modifier
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`portal-menu-${it.id}`}
                                disabled={activatingId === it.id}
                              >
                                <UserCog size={12} className="mr-1" /> Espace
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
                                Espace affilié
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              {!portal.enabled ? (
                                <DropdownMenuItem
                                  onClick={() => doActivatePortal(it)}
                                  data-testid={`portal-activate-${it.id}`}
                                >
                                  <KeyRound size={12} className="mr-2" /> Activer l&apos;espace
                                </DropdownMenuItem>
                              ) : (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => doResetPassword(it)}
                                    data-testid={`portal-reset-${it.id}`}
                                  >
                                    <RotateCcw size={12} className="mr-2" /> Réinitialiser le MDP
                                  </DropdownMenuItem>
                                  {portal.is_active ? (
                                    <DropdownMenuItem
                                      onClick={() => doTogglePortal(it, false)}
                                      data-testid={`portal-suspend-${it.id}`}
                                    >
                                      <Power size={12} className="mr-2" /> Suspendre l&apos;espace
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem
                                      onClick={() => doTogglePortal(it, true)}
                                      data-testid={`portal-reactivate-${it.id}`}
                                    >
                                      <Power size={12} className="mr-2" /> Réactiver l&apos;espace
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-red-600 focus:text-red-700 focus:bg-red-50"
                                    onClick={() => setPortalDeleteTarget(it)}
                                    data-testid={`portal-delete-${it.id}`}
                                  >
                                    <UserX size={12} className="mr-2" /> Supprimer l&apos;espace
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setDeleteTarget(it)}
                            data-testid={`delete-commission-beneficiary-${it.id}`}
                          >
                            <Trash2 size={11} />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </HorizontalScrollContainer>
      </div>

      {/* Cards mobile */}
      <div className="md:hidden space-y-2">
        {loading && <div className="text-center py-8 text-zinc-500 font-mono text-sm">Chargement…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-8 text-zinc-500 font-mono text-sm">
            {search ? "Aucun résultat" : "Aucun bénéficiaire commissions"}
          </div>
        )}
        {filtered.map((it) => {
          const portal = it.portal || {};
          return (
          <div key={it.id} className="border border-zinc-200 rounded-sm bg-white p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-zinc-900 truncate">{it.contact_name}</div>
                <div className="text-[10px] font-mono text-emerald-700 uppercase tracking-wider mt-0.5">{it.partner_id}</div>
                <div className="mt-1">
                  {!portal.enabled ? (
                    <Badge variant="outline" className="text-[10px] text-zinc-500" data-testid={`portal-status-mobile-${it.id}`}>
                      Non activé
                    </Badge>
                  ) : portal.is_active ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border text-[10px]" data-testid={`portal-status-mobile-${it.id}`}>
                      Espace actif
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-[10px]" data-testid={`portal-status-mobile-${it.id}`}>
                      Suspendu
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/consulter-affiliation/${it.id}`)}
                  className="border-emerald-200 text-emerald-700"
                  data-testid={`open-affiliate-space-mobile-${it.id}`}
                >
                  <Vault size={11} />
                </Button>
                {isAdmin && (
                  <>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`portal-menu-mobile-${it.id}`}>
                          <UserCog size={11} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
                          Espace affilié
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {!portal.enabled ? (
                          <DropdownMenuItem onClick={() => doActivatePortal(it)}>
                            <KeyRound size={12} className="mr-2" /> Activer l&apos;espace
                          </DropdownMenuItem>
                        ) : (
                          <>
                            <DropdownMenuItem onClick={() => doResetPassword(it)}>
                              <RotateCcw size={12} className="mr-2" /> Réinitialiser MDP
                            </DropdownMenuItem>
                            {portal.is_active ? (
                              <DropdownMenuItem onClick={() => doTogglePortal(it, false)}>
                                <Power size={12} className="mr-2" /> Suspendre
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => doTogglePortal(it, true)}>
                                <Power size={12} className="mr-2" /> Réactiver
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-700 focus:bg-red-50"
                              onClick={() => setPortalDeleteTarget(it)}
                            >
                              <UserX size={12} className="mr-2" /> Supprimer l&apos;espace
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button variant="outline" size="sm" onClick={() => openEdit(it)} data-testid={`edit-commission-beneficiary-mobile-${it.id}`}>
                      <Pencil size={11} />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 border-red-200" onClick={() => setDeleteTarget(it)} data-testid={`delete-commission-beneficiary-mobile-${it.id}`}>
                      <Trash2 size={11} />
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className="mt-2 space-y-1.5 text-xs font-mono text-zinc-600">
              <div className="flex items-center gap-1.5"><CalendarDays size={10} /> {dateFR(it.start_date)} → {dateFR(it.end_date)}</div>
              <div className="flex flex-wrap gap-1 pt-1">
                {(it.brands || []).map((b) => (
                  <span key={b.id} className="text-[10px] px-1.5 py-0.5 rounded-sm text-white font-mono uppercase tracking-wider" style={{ backgroundColor: b.color || "#0055FF" }}>
                    {b.name}
                  </span>
                ))}
              </div>
              <div className="pt-1.5 border-t border-zinc-100">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5 flex items-center gap-1"><Ticket size={10} /> Code affilié</p>
                <AffiliateSummary codes={it.affiliate_codes} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5 flex items-center gap-1"><MapPin size={10} /> Site affilié</p>
                <SitesSummary sites={it.partner_sites} />
              </div>
            </div>
          </div>
          );
        })}
      </div>

      <BeneficiaryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        editing={editing}
        onSaved={(res) => {
          setDialogOpen(false);
          load();
          // Si le backend a auto-activé l'espace personnel (à la création),
          // on affiche immédiatement les identifiants à transmettre au partenaire.
          if (res?.portal_credentials?.temporary_password) {
            setPortalCredentials({
              title: "Espace affilié auto-activé",
              description: "Le compte partenaire a été créé automatiquement. Transmettez ces identifiants — le mot de passe temporaire ne sera plus visible ensuite.",
              email: res.portal_credentials.email,
              temporary_password: res.portal_credentials.temporary_password,
              name: res.portal_credentials.name,
            });
          }
        }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="commission-beneficiary-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce bénéficiaire ?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;action est définitive. Le contact source restera présent dans « Contacts &amp; bénéficiaires ».
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-commission-beneficiary"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Prompt email : quand le contact n'a pas d'email enregistré */}
      <Dialog open={!!emailPromptTarget} onOpenChange={(v) => !v && setEmailPromptTarget(null)}>
        <DialogContent data-testid="portal-email-prompt">
          <DialogHeader>
            <DialogTitle>Adresse email du partenaire affilié</DialogTitle>
            <DialogDescription>
              Aucun email n&apos;est enregistré pour ce contact. Précisez l&apos;adresse à utiliser
              pour l&apos;activation de l&apos;espace affilié.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="affiliate-portal-email">Email</Label>
            <Input
              id="affiliate-portal-email"
              type="email"
              value={emailPromptTarget?.email || ""}
              onChange={(e) => setEmailPromptTarget((s) => (s ? { ...s, email: e.target.value } : s))}
              data-testid="portal-email-prompt-input"
              placeholder="partenaire@exemple.fr"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailPromptTarget(null)}>Annuler</Button>
            <Button
              onClick={() =>
                emailPromptTarget?.email &&
                doActivatePortal(emailPromptTarget.row, emailPromptTarget.email.trim())
              }
              disabled={!emailPromptTarget?.email}
              data-testid="portal-email-prompt-confirm"
            >
              <KeyRound size={13} className="mr-1.5" /> Activer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation suppression espace affilié */}
      <AlertDialog open={!!portalDeleteTarget} onOpenChange={(v) => !v && setPortalDeleteTarget(null)}>
        <AlertDialogContent data-testid="portal-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l&apos;espace affilié ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le compte utilisateur du partenaire sera supprimé et il ne pourra plus se
              connecter. Le bénéficiaire commissions et ses données sont conservés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeletePortal}
              className="bg-red-600 hover:bg-red-700"
              data-testid="portal-delete-confirm-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Identifiants générés (à copier & transmettre) */}
      <Dialog open={!!portalCredentials} onOpenChange={(v) => !v && setPortalCredentials(null)}>
        <DialogContent data-testid="portal-credentials-dialog">
          <DialogHeader>
            <DialogTitle>{portalCredentials?.title || "Identifiants générés"}</DialogTitle>
            <DialogDescription>{portalCredentials?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Email</p>
                  <p className="font-mono text-sm" data-testid="portal-credentials-email">
                    {portalCredentials?.email}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(portalCredentials?.email || "")}
                >
                  <Copy size={13} />
                </Button>
              </div>
            </div>
            <div className="border border-emerald-200 rounded-sm p-3 bg-emerald-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-mono">
                    Mot de passe temporaire
                  </p>
                  <p className="font-mono text-sm font-bold text-emerald-900" data-testid="portal-credentials-password">
                    {portalCredentials?.temporary_password}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(portalCredentials?.temporary_password || "")}
                  data-testid="portal-credentials-copy-password"
                >
                  <Copy size={13} />
                </Button>
              </div>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
              <AlertCircle size={12} className="inline mr-1" />
              Ce mot de passe ne sera plus affiché. Copiez-le maintenant.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setPortalCredentials(null)}
              data-testid="portal-credentials-close"
            >
              J&apos;ai copié les identifiants
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Dialog CRUD — miroir de RoyaltiesSection.BeneficiaryDialog
 * ────────────────────────────────────────────────────────────────── */
function BeneficiaryDialog({ open, onClose, editing, onSaved }) {
  const [contacts, setContacts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);
  const { getIdByName } = useBeneficiaryCategoryMap();
  const partnerCatId = getIdByName("Partenaire");
  const [form, setForm] = useState({
    contact_id: "",
    partner_id: "",
    partnership_category: "",
    brand_ids: [],
    start_date: todayIso(),
    end_date: "",
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [b, br] = await Promise.all([
          api.get("/beneficiaries"),
          api.get("/brands"),
        ]);
        setContacts(b.data || []);
        setBrands(br.data || []);
      } catch (e) {
        toast.error("Erreur chargement contacts / départements");
      }
    })();
    if (editing) {
      setForm({
        contact_id: editing.contact_id,
        partner_id: editing.partner_id || "",
        partnership_category: editing.partnership_category || "",
        brand_ids: editing.brand_ids || [],
        start_date: editing.start_date,
        end_date: editing.end_date,
      });
    } else {
      setForm({ contact_id: "", partner_id: "", partnership_category: "", brand_ids: [], start_date: todayIso(), end_date: "" });
    }
  }, [open, editing]);

  // Ne conserver que les contacts avec le badge « Partenaire ».
  const partnerContacts = useMemo(() => {
    if (!partnerCatId) return [];
    return contacts.filter((c) => c.category_id === partnerCatId);
  }, [contacts, partnerCatId]);

  const eligibleBrands = useMemo(() => {
    const c = partnerContacts.find((x) => x.id === form.contact_id);
    if (!c) return [];
    const ids = new Set(c.brand_ids || (c.brand_id ? [c.brand_id] : []));
    return brands.filter((b) => ids.has(b.id));
  }, [form.contact_id, partnerContacts, brands]);

  useEffect(() => {
    if (!form.contact_id) return;
    const eligibleIds = new Set(eligibleBrands.map((b) => b.id));
    setForm((prev) => ({
      ...prev,
      brand_ids: (prev.brand_ids || []).filter((id) => eligibleIds.has(id)),
    }));
  }, [form.contact_id, eligibleBrands]);

  const toggleBrand = (bid) => {
    setForm((prev) => {
      const set = new Set(prev.brand_ids || []);
      if (set.has(bid)) set.delete(bid); else set.add(bid);
      return { ...prev, brand_ids: Array.from(set) };
    });
  };

  const submit = async () => {
    if (!form.contact_id) return toast.error("Sélectionnez un contact");
    const pid = String(form.partner_id || "").trim().toUpperCase();
    if (!pid || pid.length < 3 || pid.length > 40) {
      return toast.error("ID Partenaire obligatoire (3 à 40 caractères)");
    }
    if (!form.partnership_category) {
      return toast.error("Sélectionnez une catégorie de partenariat");
    }
    if (!form.brand_ids?.length) return toast.error("Sélectionnez au moins un département");
    if (!form.start_date) return toast.error("Date de début obligatoire");
    if (form.end_date && form.end_date < form.start_date) {
      return toast.error("La date de fin doit être ≥ date de début");
    }

    setSaving(true);
    try {
      const payload = {
        contact_id: form.contact_id,
        partner_id: pid,
        partnership_category: form.partnership_category,
        brand_ids: form.brand_ids,
        start_date: form.start_date,
        end_date: form.end_date || null,
      };
      if (editing) {
        await api.put(`/commission-beneficiaries/${editing.id}`, payload);
        toast.success("Bénéficiaire mis à jour");
        onSaved();
      } else {
        const { data } = await api.post("/commission-beneficiaries", payload);
        if (data?.portal_credentials?.temporary_password) {
          toast.success("Bénéficiaire créé — espace personnel auto-activé");
        } else {
          toast.success("Bénéficiaire créé");
        }
        onSaved(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="commission-beneficiary-dialog">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Modifier le bénéficiaire" : "Nouveau bénéficiaire Commissions"}
          </DialogTitle>
          <DialogDescription>
            Sélection d&apos;un contact déjà présent dans « Contacts &amp; bénéficiaires ». L&apos;ID Partenaire est unique.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Contact bénéficiaire *</Label>
            <SearchableSelect
              value={form.contact_id}
              onChange={(v) => setForm((p) => ({ ...p, contact_id: v }))}
              options={partnerContacts.map((c) => ({
                value: c.id,
                label: c.denomination,
                keywords: c.email || c.phone || "",
                hint: c.email || undefined,
              }))}
              placeholder="Sélectionner un contact Partenaire…"
              searchPlaceholder="Rechercher un contact Partenaire…"
              disabled={!!editing}
              data-testid="commission-contact-select"
            />
            <p className="text-[10px] text-zinc-400 mt-1">
              Liste limitée aux contacts avec le badge « Partenaire » (page « Contacts &amp; bénéficiaires »).
              {partnerContacts.length === 0 && contacts.length > 0 && (
                <span className="block text-amber-600 mt-0.5">
                  Aucun contact « Partenaire » trouvé. Ajoutez le badge dans la fiche du contact.
                </span>
              )}
            </p>
            {editing && (
              <p className="text-[10px] text-zinc-400 mt-1">
                Le contact ne peut pas être modifié après création.
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs">ID Partenaire *</Label>
            <Input
              value={form.partner_id}
              onChange={(e) => setForm((p) => ({ ...p, partner_id: e.target.value.toUpperCase() }))}
              placeholder="ex : COMM-2026-001"
              maxLength={40}
              data-testid="commission-partner-id-input"
              className="font-mono uppercase"
            />
            <p className="text-[10px] text-zinc-400 mt-1">
              Identifiant unique du partenaire commissions (3 à 40 caractères).
            </p>
          </div>

          <div>
            <Label className="text-xs">Catégorie partenariat *</Label>
            <select
              value={form.partnership_category}
              onChange={(e) => setForm((p) => ({ ...p, partnership_category: e.target.value }))}
              data-testid="commission-partnership-category-select"
              className="mt-1 w-full h-9 rounded-md border border-zinc-300 bg-white px-2 text-xs text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
            >
              <option value="">— Sélectionner —</option>
              {PARTNERSHIP_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-zinc-400 mt-1">
              Catégorise la relation partenariale (obligatoire).
            </p>
          </div>

          <div>
            <Label className="text-xs">Départements de rattachement *</Label>
            {!form.contact_id ? (
              <p className="text-xs text-zinc-400 italic py-2">
                Sélectionnez d&apos;abord un contact.
              </p>
            ) : eligibleBrands.length === 0 ? (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs text-amber-800">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span>Ce contact n&apos;est rattaché à aucun département. Modifiez-le dans la page « Contacts &amp; bénéficiaires ».</span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 py-1" data-testid="commission-brands-picker">
                {eligibleBrands.map((b) => {
                  const active = (form.brand_ids || []).includes(b.id);
                  return (
                    <button
                      type="button"
                      key={b.id}
                      onClick={() => toggleBrand(b.id)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        active
                          ? "bg-[#0055FF] text-white border-[#0055FF]"
                          : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                      }`}
                      data-testid={`commission-brand-toggle-${b.id}`}
                    >
                      {b.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date de début *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
                data-testid="commission-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">Date de fin</Label>
              <Input
                type="date"
                value={form.end_date || ""}
                onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
                data-testid="commission-end-date"
                placeholder="Optionnel"
              />
              <p className="text-[10px] text-zinc-500 mt-0.5 font-mono">
                Laisser vide pour un contrat à durée indéterminée
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-[#0055FF] hover:bg-[#0044CC]"
            data-testid="save-commission-beneficiary-btn"
          >
            <Save size={13} className="mr-1" />
            {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Résumé des commissions Code d'affiliation (auto)
 * ────────────────────────────────────────────────────────────────── */
function AffiliateSummary({ codes = [] }) {
  const activeCodes = (codes || []).filter((c) => c.is_active !== false && c.manual_active !== false);
  if (!activeCodes.length) {
    return <span className="text-xs text-zinc-400 italic">Aucun code affilié</span>;
  }
  return (
    <div className="flex flex-col gap-1.5" data-testid="affiliate-summary">
      {activeCodes.map((c) => (
        <div key={c.id} className="text-xs">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5 uppercase tracking-wider text-[10px]">
              {c.code}
            </span>
            {c.commission_value != null && c.commission_type ? (
              <span className="font-mono text-zinc-700">
                {c.commission_type === "percent"
                  ? `${Number(c.commission_value).toFixed(2)} %`
                  : `${Number(c.commission_value).toFixed(2)} €`}
              </span>
            ) : (c.service_commissions || []).length > 0 ? (
              <span className="font-mono text-[10px] text-zinc-500">
                {(c.service_commissions || []).length} tarif{(c.service_commissions || []).length > 1 ? "s" : ""} par service
              </span>
            ) : (
              <span className="font-mono text-[10px] text-zinc-400">— non défini</span>
            )}
          </div>
          {(c.service_commissions || []).length > 0 && c.commission_value == null && (
            <ul className="mt-0.5 ml-2 space-y-0.5">
              {(c.service_commissions || []).slice(0, 3).map((sc, i) => (
                <li key={i} className="text-[10px] font-mono text-zinc-600 truncate">
                  · {sc.service_name || "Service"} :{" "}
                  {sc.commission_type === "percent"
                    ? `${Number(sc.commission_value ?? 0).toFixed(2)} %`
                    : `${Number(sc.commission_value ?? 0).toFixed(2)} €`}
                </li>
              ))}
              {(c.service_commissions || []).length > 3 && (
                <li className="text-[10px] font-mono text-zinc-400">
                  · +{(c.service_commissions || []).length - 3} autre(s)…
                </li>
              )}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Résumé des commissions Site affilié (auto)
 * ────────────────────────────────────────────────────────────────── */
function SitesSummary({ sites = [] }) {
  if (!sites || !sites.length) {
    return <span className="text-xs text-zinc-400 italic">Aucun site affilié</span>;
  }
  const typeLabel = (t) => ({ agency: "Agence", bureau: "Bureau", pos: "Point de vente", other: "Site" }[t] || "Site");
  return (
    <div className="flex flex-col gap-1.5" data-testid="sites-summary">
      {sites.slice(0, 4).map((s) => {
        const value = s.commission_amount ?? s.commission_value;
        const isPercent = s.commission_type === "percent";
        return (
          <div key={s.id} className="text-xs flex items-center gap-1.5 flex-wrap">
            <span className="font-mono font-semibold text-sky-800 bg-sky-50 border border-sky-200 rounded-sm px-1.5 py-0.5 text-[10px]">
              {typeLabel(s.type)}
            </span>
            <span className="text-zinc-700 truncate max-w-[160px]" title={s.name}>{s.name}</span>
            {value != null && value !== "" ? (
              <span className="font-mono text-zinc-700">
                {isPercent
                  ? `${Number(value).toFixed(2)} %`
                  : `${Number(value).toFixed(2)} €`}
              </span>
            ) : (
              <span className="font-mono text-[10px] text-zinc-400">— non défini</span>
            )}
          </div>
        );
      })}
      {sites.length > 4 && (
        <span className="text-[10px] font-mono text-zinc-400">+{sites.length - 4} autre(s) site(s)…</span>
      )}
    </div>
  );
}

